import CssBaseline from "@mui/material/CssBaseline/CssBaseline";
import { createTheme, Shadows, ThemeProvider } from "@mui/material/styles";
import { THEME, useTonConnectUI } from "@tonconnect/ui-react";
import React, { FC, PropsWithChildren, useEffect, useState } from "react";

export type ThemeType = "light" | "dark";
export type SetTheme = (theme: ThemeType) => void;

export const ThemeContext = React.createContext<[ThemeType, SetTheme]>([
  undefined!,
  () => null,
]);

const lightTheme = createTheme({
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
  palette: {
    primary: {
      main: "#31A6F5",
    },
  },
  shape: {
    borderRadius: 20,
  },
  shadows: ["none"].concat(
    new Array(24).fill("0 4px 24px rgb(0 0 0 / 16%)")
  ) as Shadows,
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          wordWrap: "break-word",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingBottom: "16px !important",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === "contained"
            ? {
                color: "#fff",
              }
            : {
                color: "#000",
              }),
          textTransform: "none",
          fontWeight: "590",
          fontSize: "15px",
        }),
      },
    },
  },
});

export const darkTheme = createTheme({
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      "'Roboto'",
      '"Helvetica Neue"',
      "Arial",
      "Tahoma",
      "Verdana",
      "sans-serif",
    ].join(","),
  },
  palette: {
    mode: "dark",
    background: {
      paper: "rgb(15 23 42)",
    },
    primary: {
      main: "#31A6F5",
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: ["none"].concat(
    new Array(24).fill("0 4px 24px rgb(0 0 0 / 16%)")
  ) as Shadows,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          color: "#FFF",
          fontWeight: "590",
          fontSize: "15px",
        },
      },
    },
  },
});

// const getTheme = (): ThemeType => {
//   if (
//     localStorage.getItem("color-theme") === "dark" ||
//     (!("color-theme" in localStorage) &&
//       window.matchMedia("(prefers-color-scheme: dark)").matches)
//   ) {
//     return "dark";
//   }
//   return "light";
// };

export const AppThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [, setOptions] = useTonConnectUI();

  const setter = useState<"light" | "dark">("light");
  const [theme] = setter;
  useEffect(() => {
    if (theme === "dark") {
      setOptions({ uiPreferences: { theme: THEME.DARK } });
      document.documentElement.classList.add("dark");
    } else {
      setOptions({ uiPreferences: { theme: THEME.LIGHT } });
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("color-theme", theme);
  }, [theme, setOptions]);

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <ThemeContext.Provider value={setter}>{children}</ThemeContext.Provider>
    </ThemeProvider>
  );
};
